import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import ProtocolCalendar from "@/components/ProtocolCalendar";
import { api } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Flame, Zap, Heart, Brain } from "lucide-react";

const ICONS = { energy: Zap, hormones: Flame, recovery: Heart, mindset: Brain };
const CAT_META = {
  energy:   { label: "Energy", color: "#FF8A00" },
  hormones: { label: "Hormones", color: "#E52E71" },
  recovery: { label: "Recovery", color: "#FFB347" },
  mindset:  { label: "Mindset", color: "#FFD58A" },
};

export default function Protocol() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, h] = await Promise.all([
        api.get("/protocol/today"),
        api.get("/protocol/history?days=90"),
      ]);
      setData(p.data);
      setHistory(h.data);
    })();
  }, []);

  const reloadHistory = async () => {
    try {
      const h = await api.get("/protocol/history?days=90");
      setHistory(h.data);
    } catch { /* ignore */ }
  };

  const toggle = async (item) => {
    const next = !item.completed;
    const updated = data.items.map((i) => i.key === item.key ? { ...i, completed: next } : i);
    setData({ ...data, items: updated, completed_count: updated.filter((i) => i.completed).length });
    try {
      await api.post("/protocol/check", { item_key: item.key, completed: next });
      if (next && updated.every((i) => i.completed)) {
        toast.success("Day complete. Streak +1. 🔥");
        refresh();
      }
      reloadHistory();
    } catch { toast.error("Save failed"); }
  };

  if (!data) return <AppShell><div className="text-white/50">Loading…</div></AppShell>;

  const byCat = {};
  data.items.forEach((i) => { (byCat[i.category] ||= []).push(i); });

  return (
    <AppShell>
      <div className="space-y-10">
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Daily Protocol</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Today is the protocol.</h1>
          <p className="text-white/55 mt-2">Simple. Daily. Compounding.</p>
        </div>

        {!data.checked_in_today && (data.days_since_last_checkin ?? 0) >= 1 && (
          <div
            data-testid="protocol-past-due"
            className={`fade-up glass rounded-2xl p-5 border flex items-center gap-4 ${
              data.days_since_last_checkin >= 3
                ? "border-red-500/30 bg-red-500/[0.04]"
                : "border-[#FF8A00]/25 bg-[#FF8A00]/[0.04]"
            }`}
          >
            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
              data.days_since_last_checkin >= 3
                ? "bg-red-500/15 text-red-400"
                : "bg-[#FF8A00]/15 text-[#FF8A00]"
            }`}>
              <Flame size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white/85 font-medium">
                {data.days_since_last_checkin >= 3
                  ? `${data.days_since_last_checkin} days past due`
                  : `${data.days_since_last_checkin} day since your last check-in`}
              </div>
              <div className="text-xs text-white/50 mt-0.5">
                {data.last_checkin_day
                  ? `Last logged on ${data.last_checkin_day}. Small consistency wins the long game.`
                  : "Start today — check off any item below to begin your streak."}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {Object.entries(byCat).map(([cat, items], idx) => {
            const meta = CAT_META[cat];
            const Icon = ICONS[cat];
            const done = items.filter((i) => i.completed).length;
            return (
              <div key={cat} className={`glass rounded-2xl p-6 fade-up delay-${idx+1}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                         style={{ background: `${meta.color}20`, color: meta.color }}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-display text-2xl">{meta.label}</div>
                      <div className="text-[11px] text-white/40">{done}/{items.length} complete</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((it) => (
                    <button
                      key={it.key}
                      onClick={() => toggle(it)}
                      data-testid={`full-protocol-${it.key}`}
                      className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${
                        it.completed
                          ? "bg-[#FF8A00]/8 border-[#FF8A00]/25"
                          : "bg-[#0A0A0A] border-white/5 hover:border-white/15"
                      }`}
                    >
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        it.completed ? "bg-gradient-to-br from-[#FF8A00] to-[#E52E71] check-pop" : "border border-white/20"
                      }`}>
                        {it.completed && <CheckCircle2 size={12} />}
                      </div>
                      <div>
                        <div className="text-sm">{it.label}</div>
                        <div className="text-[11px] text-white/40 mt-1">{it.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Monthly calendar view */}
        {history && (
          <ProtocolCalendar entries={history.entries} stats={history.stats} />
        )}
      </div>
    </AppShell>
  );
}
